import { useState } from 'react'
import type { UUID } from '../types'

export interface FieldDef {
  key: string
  label: string
  type?: string
  required?: boolean
  options?: string[]
}

interface EntityTableProps<T extends { id: UUID }> {
  title: string
  columns: string[]
  rows: T[]
  renderRow: (row: T, onDelete: () => void) => React.ReactNode
  onAdd: (data: Record<string, unknown>) => void
  addFields: FieldDef[]
  extraActions?: React.ReactNode
}

export default function EntityTable<T extends { id: UUID }>({
  title,
  columns,
  rows,
  renderRow,
  onAdd,
  addFields,
  extraActions,
}: EntityTableProps<T>) {
  const [adding, setAdding] = useState(false)
  const [formData, setFormData] = useState<Record<string, unknown>>({})

  const handleSave = () => {
    onAdd(formData)
    setAdding(false)
    setFormData({})
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {extraActions}
          <button
            onClick={() => {
              setAdding(true)
              setFormData({})
            }}
            className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            + Add
          </button>
        </div>
      </div>
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {columns.map((c) => (
                <th
                  key={c}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {c}
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {adding && (
              <tr className="bg-blue-50 dark:bg-blue-900/20">
                {addFields.map((f) => (
                  <td key={f.key} className="px-4 py-2">
                    {f.type === 'select' && f.options ? (
                      <select
                        value={(formData[f.key] as string) ?? ''}
                        onChange={(e) => setFormData((p) => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">—</option>
                        {f.options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : f.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={!!formData[f.key]}
                        onChange={(e) => setFormData((p) => ({ ...p, [f.key]: e.target.checked }))}
                        className="h-4 w-4"
                      />
                    ) : (
                      <input
                        type={f.type || 'text'}
                        value={(formData[f.key] as string | number) ?? ''}
                        onChange={(e) => setFormData((p) => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.label}
                        required={f.required}
                        className="w-full border rounded px-2 py-1 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      />
                    )}
                  </td>
                ))}
                <td className="px-4 py-2">
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setAdding(false)
                        setFormData({})
                      }}
                      className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((row) => renderRow(row, () => {}))}
            {rows.length === 0 && !adding && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
